type BinaryEncodable = {
	[key: string]: number | string | boolean;
};

const encodeUrl = <T extends BinaryEncodable>(data: T): string => {
	const entries = Object.entries(data).sort(([a], [b]) => a.localeCompare(b));

	const buffers: Uint8Array[] = [];
	let totalLength = 0;

	// Pack property count in 4 bits (supports up to 15 properties)
	let header = entries.length;

	// Process each property
	for (const [key, value] of entries) {
		// Encode key with variable length encoding
		const keyBuffer = new TextEncoder().encode(key);
		if (keyBuffer.length > 31)
			throw new Error('Key too long (max 31 chars)');

		// Pack key length in 5 bits
		const keyLengthByte = keyBuffer.length;
		buffers.push(new Uint8Array([keyLengthByte]), keyBuffer);
		totalLength += 1 + keyBuffer.length;

		// Encode value with type info packed into the type byte
		if (typeof value === 'number') {
			// Check if it's an integer that fits in smaller types
			if (Number.isInteger(value)) {
				if (value >= 0 && value <= 255) {
					buffers.push(new Uint8Array([0x10, value])); // 0x10 = uint8
					totalLength += 2;
				} else if (value >= -128 && value <= 127) {
					buffers.push(new Uint8Array([0x11, value & 0xff])); // 0x11 = int8
					totalLength += 2;
				} else if (value >= -32768 && value <= 32767) {
					const buf = new Uint8Array(3);
					buf[0] = 0x12; // int16
					new DataView(buf.buffer, 1).setInt16(0, value);
					buffers.push(buf);
					totalLength += 3;
				} else if (value >= 0 && value <= 65535) {
					const buf = new Uint8Array(3);
					buf[0] = 0x13; // uint16
					new DataView(buf.buffer, 1).setUint16(0, value);
					buffers.push(buf);
					totalLength += 3;
				} else if (value >= -2147483648 && value <= 2147483647) {
					const buf = new Uint8Array(5);
					buf[0] = 0x14; // int32
					new DataView(buf.buffer, 1).setInt32(0, value);
					buffers.push(buf);
					totalLength += 5;
				} else {
					const buf = new Uint8Array(9);
					buf[0] = 0x15; // float64
					new DataView(buf.buffer, 1).setFloat64(0, value);
					buffers.push(buf);
					totalLength += 9;
				}
			} else {
				// Use float32 if precision allows it
				const float32 = Math.fround(value);
				if (float32 === value) {
					const buf = new Uint8Array(5);
					buf[0] = 0x16; // float32
					new DataView(buf.buffer, 1).setFloat32(0, value);
					buffers.push(buf);
					totalLength += 5;
				} else {
					const buf = new Uint8Array(9);
					buf[0] = 0x15; // float64
					new DataView(buf.buffer, 1).setFloat64(0, value);
					buffers.push(buf);
					totalLength += 9;
				}
			}
		} else if (typeof value === 'string') {
			// UUID detection and compact encoding
			if (
				/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
					value,
				)
			) {
				const hex = value.replace(/-/g, '');
				const uuidBuffer = new Uint8Array(17);
				uuidBuffer[0] = 0x20; // UUID type
				for (let i = 0; i < 16; i++) {
					uuidBuffer[i + 1] = parseInt(hex.substr(i * 2, 2), 16);
				}
				buffers.push(uuidBuffer);
				totalLength += 17;
			} else {
				const strBuffer = new TextEncoder().encode(value);
				if (strBuffer.length <= 255) {
					buffers.push(
						new Uint8Array([0x21, strBuffer.length]),
						strBuffer,
					); // Short string
					totalLength += 2 + strBuffer.length;
				} else {
					const lengthBuf = new Uint8Array(3);
					lengthBuf[0] = 0x22; // Long string
					new DataView(lengthBuf.buffer, 1).setUint16(
						0,
						strBuffer.length,
					);
					buffers.push(lengthBuf, strBuffer);
					totalLength += 3 + strBuffer.length;
				}
			}
		} else if (typeof value === 'boolean') {
			buffers.push(new Uint8Array([value ? 0x31 : 0x30])); // 0x30=false, 0x31=true
			totalLength += 1;
		}
	}

	// Combine all buffers with header
	const combined = new Uint8Array(totalLength + 1);
	combined[0] = header;
	let offset = 1;
	for (const buffer of buffers) {
		combined.set(buffer, offset);
		offset += buffer.length;
	}

	// Convert to URL-safe base64
	return btoa(String.fromCharCode(...combined))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '');
};

const decodeUrl = <T extends BinaryEncodable>(encoded: string): T => {
	try {
		// Convert from URL-safe base64
		const binaryStr = atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
		const bytes = new Uint8Array(binaryStr.length);
		for (let i = 0; i < binaryStr.length; i++) {
			bytes[i] = binaryStr.charCodeAt(i);
		}

		let offset = 0;
		const result: Record<string, any> = {};

		// Read property count from header
		const propertyCount = bytes[offset++];

		for (let i = 0; i < propertyCount; i++) {
			// Read key
			const keyLength = bytes[offset++];
			const key = new TextDecoder().decode(
				bytes.subarray(offset, offset + keyLength),
			);
			offset += keyLength;

			// Read value type
			const valueType = bytes[offset++];

			// Decode based on type
			if ((valueType & 0xf0) === 0x10) {
				// Number types
				switch (valueType) {
					case 0x10: // uint8
						result[key] = bytes[offset++];
						break;
					case 0x11: // int8
						result[key] = new DataView(
							bytes.buffer,
							offset,
							1,
						).getInt8(0);
						offset += 1;
						break;
					case 0x12: // int16
						result[key] = new DataView(
							bytes.buffer,
							offset,
							2,
						).getInt16(0);
						offset += 2;
						break;
					case 0x13: // uint16
						result[key] = new DataView(
							bytes.buffer,
							offset,
							2,
						).getUint16(0);
						offset += 2;
						break;
					case 0x14: // int32
						result[key] = new DataView(
							bytes.buffer,
							offset,
							4,
						).getInt32(0);
						offset += 4;
						break;
					case 0x15: // float64
						result[key] = new DataView(
							bytes.buffer,
							offset,
							8,
						).getFloat64(0);
						offset += 8;
						break;
					case 0x16: // float32
						result[key] = new DataView(
							bytes.buffer,
							offset,
							4,
						).getFloat32(0);
						offset += 4;
						break;
				}
			} else if ((valueType & 0xf0) === 0x20) {
				// String types
				switch (valueType) {
					case 0x20: // UUID
						const hex = Array.from(
							bytes.subarray(offset, offset + 16),
						)
							.map((b) => b.toString(16).padStart(2, '0'))
							.join('');
						result[key] = `${hex.slice(0, 8)}-${hex.slice(
							8,
							12,
						)}-${hex.slice(12, 16)}-${hex.slice(
							16,
							20,
						)}-${hex.slice(20)}`;
						offset += 16;
						break;
					case 0x21: // Short string
						const shortStrLength = bytes[offset++];
						result[key] = new TextDecoder().decode(
							bytes.subarray(offset, offset + shortStrLength),
						);
						offset += shortStrLength;
						break;
					case 0x22: // Long string
						const longStrLength = new DataView(
							bytes.buffer,
							offset,
							2,
						).getUint16(0);
						offset += 2;
						result[key] = new TextDecoder().decode(
							bytes.subarray(offset, offset + longStrLength),
						);
						offset += longStrLength;
						break;
				}
			} else if ((valueType & 0xf0) === 0x30) {
				// Boolean types
				result[key] = valueType === 0x31;
			}
		}

		return result as T;
	} catch (e) {
		console.error('Decoding failed:', e);
		return {} as T;
	}
};

export { encodeUrl, decodeUrl };
